import React, { useEffect, useContext, useState, useCallback, useReducer, useMemo, Fragment } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import ZoomVideo, { ConnectionState, ReconnectReason } from '@zoom/videosdk';
import { message, Modal } from 'antd';
import 'antd/dist/antd.min.css';
import produce from 'immer';
import Home from './feature/home/home';
import Video from './feature/video/video';
import VideoSingle from './feature/video/video-single';
import VideoNonSAB from './feature/video/video-non-sab';
import Preview from './feature/preview/preview';
import ZoomContext from './context/zoom-context';
import ZoomMediaContext from './context/media-context';
import ChatContext from './context/chat-context';
import CommandContext from './context/cmd-context';
import RecordingContext from './context/recording-context';
import LoadingLayer from './component/loading-layer';
import Chat from './feature/chat/chat';
import Command from './feature/command/command';
//import Subsession from './feature/subsession/subsession';
//import { ChatClient, CommandChannelClient, MediaStream, RecordingClient, SubsessionClient } from './index-types';
import { ChatClient, CommandChannelClient, MediaStream, RecordingClient } from './index-types';
import './App.css';
//import SubsessionContext from './context/subsession-context';
import { isAndroidBrowser } from './utils/platform';


import { Box, Button, CircularProgress, Dialog, Grid, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CallAudio from "../audio/call.mp3";
import Phone1 from "../img/phone2.gif";
import { urlBase } from "../settings/urlConfig";

import { ChatRecord } from "./feature/chat/chat-types";
import { useCallCenter } from "../context/CallCenterContext";
import { useLayout } from "../context/LayoutContext";

interface AppProps {
    meetingArgs: {
        sdkKey: string;
        topic: string;
        signature: string;
        name: string;
        password?: string;
        webEndpoint?: string;
        enforceGalleryView?: string;
    };
}
const mediaShape = {
    audio: {
        encode: false,
        decode: false,
    },
    video: {
        encode: false,
        decode: false,
    },
    share: {
        encode: false,
        decode: false,
    },
};
const mediaReducer = produce((draft, action) => {
    switch (action.type) {
        case "audio-encode": {
            draft.audio.encode = action.payload;
            break;
        }
        case "audio-decode": {
            draft.audio.decode = action.payload;
            break;
        }
        case "video-encode": {
            draft.video.encode = action.payload;
            break;
        }
        case "video-decode": {
            draft.video.decode = action.payload;
            break;
        }
        case "share-encode": {
            draft.share.encode = action.payload;
            break;
        }
        case "share-decode": {
            draft.share.decode = action.payload;
            break;
        }
        case "reset-media": {
            Object.assign(draft, { ...mediaShape });
            break;
        }
        default:
            break;
    }
}, mediaShape);

declare global {
    interface Window {
        webEndpoint: string | undefined;
        zmClient: any | undefined;
        mediaStream: any | undefined;
        crossOriginIsolated: boolean;
    }
}

interface MeditocChannelData<T> {
    identifierKey: string;
    description: string;
    data: T;
}
interface MeditocUserData {
    sMotivoConsulta: string;
    sCorreo: string;
    sFolio: string;
}

const audioCall = new Audio(CallAudio);
audioCall.loop = true;

function ZoomMainApp(props: AppProps) {
    const {
        meetingArgs: { sdkKey, topic, signature, name, password, webEndpoint: webEndpointArg, enforceGalleryView }
    } = props;
    const [loading, setIsLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('');
    const [isFailover, setIsFailover] = useState<boolean>(false);
    const [status, setStatus] = useState<string>('closed');
    const [mediaState, dispatch] = useReducer(mediaReducer, mediaShape);
    const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
    const [chatClient, setChatClient] = useState<ChatClient | null>(null);
    const [recordingClient, setRecordingClient] = useState<RecordingClient | null>(null);
    const [commandClient, setCommandClient] = useState<CommandChannelClient | null>(null);
    //const [subsessionClient, setSubsessionClient] = useState<SubsessionClient | null>(null);
    const [isSupportGalleryView, setIsSupportGalleryView] = useState<boolean>(true);
    const zmClient = useContext(ZoomContext);
    let webEndpoint: any;
    if (webEndpointArg) {
        webEndpoint = webEndpointArg;
    } else {
        webEndpoint = window?.webEndpoint ?? 'zoom.us';
    }
    const mediaContext = useMemo(() => ({ ...mediaState, mediaStream }), [mediaState, mediaStream]);
    const galleryViewWithoutSAB = Number(enforceGalleryView) === 1 && !window.crossOriginIsolated;


    const [isFullScreen, setIsFullScreen] = useState(false);
    const { handleOpenAlert } = useLayout();
    const [isMotivoConsulta, setIsMotivoConsulta] = useState<boolean>(false);
    const [sMotivoConsulta, setSMotivoConsulta] = useState("");
    const { triggerForRemoveAllParticipans, handleChangePacienteEnSala, fnActualizarEstatus } = useCallCenter();
    const [chatRecords, setChatRecords] = useState<ChatRecord[]>([]);
    const [triggerOnPatientConnect, setTriggerOnPatientConnect] = useState<boolean | null>(null);
    const [modalOpenPatientConnecting, setModalOpenPatientConnecting] = useState(false);

    const [medicIsInChat, setMedicIsInChat] = useState(false);
    const [patientIsInChat, setPatientIsInChat] = useState(false);
    // const [audioCall, setAudioCall] = useState(getAudioCall());

    const handleChangeIsFullScreen = (value: boolean) => {
        setIsFullScreen(value);
    };

    useEffect(() => {
        const init = async () => {
            await zmClient.init("en-US",
                'Global',
                {
                    webEndpoint,
                    enforceMultipleVideos: galleryViewWithoutSAB,
                    stayAwake: true
                });
            try {
                setLoadingText("Uniendose a la sesión...");
                await zmClient.join(topic, signature, name, password).catch((e) => {
                    // console.log(e);
                });
                const stream = zmClient.getMediaStream();
                setMediaStream(stream);
                setIsSupportGalleryView(stream.isSupportMultipleVideos() && !isAndroidBrowser());
                const chatClient = zmClient.getChatClient();
                const commandClient = zmClient.getCommandClient();
                const recordingClient = zmClient.getRecordingClient();
                //const ssClient = zmClient.getSubsessionClient();
                setChatClient(chatClient);
                setCommandClient(commandClient);
                setRecordingClient(recordingClient);
                //setSubsessionClient(ssClient);
                setIsLoading(false);

                setIsMotivoConsulta(false);
                setSMotivoConsulta("");
                setChatRecords([]);
            } catch (e: any) {
                // console.log("Error joining meeting", e);
                setIsLoading(false);
                message.error(e.reason);
            }
        };
        init();
        return () => {
            ZoomVideo.destroyClient();
        };
    }, [sdkKey, signature, zmClient, topic, name, password, webEndpoint, galleryViewWithoutSAB]);
    const onConnectionChange = useCallback(
        (payload) => {
            if (payload.state === ConnectionState.Reconnecting) {
                setIsLoading(true);
                setIsFailover(true);
                setStatus("connecting");
                const { reason } = payload;
                //const { reason, subsessionName } = payload;
                if (reason === ReconnectReason.Failover) {
                    setLoadingText('Session Disconnected,Try to reconnect');
                    //} else if (reason === ReconnectReason.JoinSubsession || reason === ReconnectReason.MoveToSubsession) {
                    //    setLoadingText(`Joining ${subsessionName}...`);
                } else if (reason === ReconnectReason.BackToMainSession) {
                    setLoadingText('Returning to Main Session...');
                }
            } else if (payload.state === ConnectionState.Connected) {
                setStatus("connected");
                if (isFailover) {
                    setIsLoading(false);
                }
                window.zmClient = zmClient;
                window.mediaStream = zmClient.getMediaStream();
            } else if (payload.state === ConnectionState.Closed) {
                setStatus("closed");
                dispatch({ type: "reset-media" });
                if (payload.reason === "ended by host") {
                    Modal.warning({
                        title: "Meeting ended",
                        content: "This meeting has been ended by host",
                    });
                }
            }
        },
        [isFailover, zmClient]
    );
    const onMediaSDKChange = useCallback((payload) => {
        const { action, type, result } = payload;
        dispatch({ type: `${type}-${action}`, payload: result === "success" });
    }, []);

    const onDialoutChange = useCallback((payload) => {
        // console.log("onDialoutChange", payload);
    }, []);

    const onAudioMerged = useCallback((payload) => {
        // console.log("onAudioMerged", payload);
    }, []);

    const onLeaveOrJoinSession = useCallback(async () => {
        handleChangePacienteEnSala(false);
        if (status === "closed") {
            setIsLoading(true);
            //await zmClient.leave();
            await zmClient.join(topic, signature, name, password);
            setIsLoading(false);
        } else if (status === "connected") {
            await zmClient.leave();
            message.warn("You have left the session.");
        }
        //eslint-disable-next-line
    }, [zmClient, status, topic, signature, name, password]);

    const fnExpulsarTodos = async (reconnect: boolean = true) => {
        try {
            await zmClient.leave(true);
            if (reconnect) {
                setTimeout(async () => {
                    await zmClient.leave();
                    await zmClient.join(topic, signature, name, password);
                    zmClient.on("connection-change", onConnectionChange);
                    zmClient.on("media-sdk-change", onMediaSDKChange);
                    zmClient.on("dialout-state-change", onDialoutChange);
                    zmClient.on("merged-audio", onAudioMerged);
                    zmClient.on("command-channel-message", onCommandChannelMessage);
                    setStatus("connected");
                    localStorage.removeItem("sFolio");
                    setIsMotivoConsulta(false);
                    setSMotivoConsulta("");
                    setChatRecords([]);
                    handleChangePacienteEnSala(false);
                }, 1000);
            }
        }
        catch (e) {

        }
    };

    const fnRemoveFrames = () => {
        const frames = document.getElementsByTagName("iframe");
        if (frames) {
            for (let index = 0; index < frames.length; index++) {
                frames.item(index)?.remove();
            }
        }
    };

    const onCommandChannelMessage = async (payload: any) => {
        // console.log("command chanel", payload);
        if (payload) {
            try {
                // console.log("textChannel", payload.text);
                if (payload.text.includes("632e6afb-d2b8-4b2f-ad90-de5d23640111")) {
                    await fnActualizarEstatus(false, true);
                    const datosPaciente: MeditocChannelData<MeditocUserData> = JSON.parse(payload.text);
                    if (datosPaciente) {
                        localStorage.setItem("sFolio", JSON.stringify(datosPaciente.data));
                        if (datosPaciente.data.sMotivoConsulta) {
                            setIsMotivoConsulta(true);
                            setSMotivoConsulta(datosPaciente.data.sMotivoConsulta);
                        } else {
                            setIsMotivoConsulta(false);
                            setSMotivoConsulta("");
                        }
                        handleChangePacienteEnSala(true);
                    }
                    return;
                }

                if (payload.text.includes("68202896-affa-42db-a1db-0a1aa345339c")) {
                    await fnActualizarEstatus(false, true);
                    handleChangePacienteEnSala(false);
                    const chatClienttmp = zmClient.getChatClient();
                    chatClienttmp?.sendToAll("El paciente ha salido de la sala desde la APP y ha regresado al menú principal. Finalice la consulta o Recicle el folio.");
                    handleOpenAlert(
                        "El paciente ha salido de la sala desde la APP y ha regresado al menú principal. Finalice la consulta o Recicle el folio."
                    );
                    return;
                }

                if (payload.text.includes("d86bdd83-7944-4106-9a45-5f3b90963429")) {
                    setTriggerOnPatientConnect(true);
                    await fnActualizarEstatus(false, true);
                    handleChangePacienteEnSala(true);
                    handleOpenAlert("Llamada de paciente entrante", "info");
                    setModalOpenPatientConnecting(true);
                    audioCall.play();
                    return;
                }
            } catch (error) {
                // console.log(error);
            }
        }
    };

    const handleClickAceptarLlamada = () => {
        setTriggerOnPatientConnect(false);
        setModalOpenPatientConnecting(false);
        commandClient?.send("LLAMADAENLAZADA");
        chatClient?.sendToAll("Llamada enlazada con el paciente");
        audioCall.pause();
    };

    useEffect(() => {
        zmClient.on("connection-change", onConnectionChange);
        zmClient.on("media-sdk-change", onMediaSDKChange);
        zmClient.on("dialout-state-change", onDialoutChange);
        zmClient.on("merged-audio", onAudioMerged);
        zmClient.on("command-channel-message", onCommandChannelMessage);
        return () => {
            zmClient.off('connection-change', onConnectionChange);
            zmClient.off('media-sdk-change', onMediaSDKChange);
            zmClient.off('dialout-state-change', onDialoutChange);
            zmClient.off('merged-audio', onAudioMerged);
            zmClient.on("command-channel-message", onCommandChannelMessage);
        };
    }, [zmClient, onConnectionChange, onMediaSDKChange, onDialoutChange, onAudioMerged]);

    useEffect(() => {
        if (zmClient) {
            fnExpulsarTodos(triggerForRemoveAllParticipans !== null);
            // console.log("Expulsar todos");
        }
    }, [triggerForRemoveAllParticipans]);

    return (
        <Fragment>
            <div className="App">
                {loading && <LoadingLayer content={loadingText} />}
                {!loading && (
                    <ZoomMediaContext.Provider value={mediaContext}>
                        <ChatContext.Provider value={chatClient}>
                            <RecordingContext.Provider value={recordingClient}>
                                <CommandContext.Provider value={commandClient}>
                                    <Grid container spacing={2}>
                                        {status === "closed" ? (
                                            <Fragment>
                                                <Grid item xs={12}>
                                                    <Typography>Reiniciando sala. Espera un momento...</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <CircularProgress color="primary" />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        Si la sala no inicia automáticamente. Da clic aquí
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={onLeaveOrJoinSession}
                                                    >
                                                        Iniciar sala
                                                    </Button>
                                                </Grid>
                                            </Fragment>
                                        ) : (
                                            <Fragment>
                                                <Fragment>
                                                    <div
                                                        style={
                                                            isFullScreen
                                                                ? {
                                                                    position: "fixed",
                                                                    zIndex: 2000,
                                                                    inset: 0,
                                                                    display: "flex",
                                                                }
                                                                : { position: "relative", width: "100%" }
                                                        }
                                                    >
                                                        <div style={isFullScreen ? { flex: 1 } : {}}>
                                                            {isSupportGalleryView ? (
                                                                <Video
                                                                    isFullScreen={isFullScreen}
                                                                    handleChangeIsFullScreen={handleChangeIsFullScreen}
                                                                    triggerOnPatientConnect={triggerOnPatientConnect}
                                                                />
                                                            ) : galleryViewWithoutSAB ? (
                                                                <VideoNonSAB
                                                                    isFullScreen={isFullScreen}
                                                                    handleChangeIsFullScreen={handleChangeIsFullScreen}
                                                                    triggerOnPatientConnect={triggerOnPatientConnect}
                                                                />
                                                            ) : (
                                                                <VideoSingle
                                                                    isFullScreen={isFullScreen}
                                                                    handleChangeIsFullScreen={handleChangeIsFullScreen}
                                                                    triggerOnPatientConnect={triggerOnPatientConnect}
                                                                />
                                                            )}
                                                        </div>
                                                        {isMotivoConsulta && !isFullScreen && (
                                                            <Alert severity="info">
                                                                Motivo de consulta: {sMotivoConsulta}
                                                            </Alert>
                                                        )}
                                                        <div style={isFullScreen ? { width: 300 } : {}}>
                                                            <Chat
                                                                isFullScreen={isFullScreen}
                                                                fnRemoveFrames={fnRemoveFrames}
                                                                chatRecords={chatRecords}
                                                                setChatRecords={setChatRecords}
                                                                setMedicIsInChat={setMedicIsInChat}
                                                                medicIsInChat={medicIsInChat}
                                                                patientIsInChat={patientIsInChat}
                                                                setPatientIsInChat={setPatientIsInChat}
                                                            />
                                                        </div>
                                                    </div>
                                                    <Grid item xs={12}></Grid>
                                                </Fragment>
                                            </Fragment>
                                        )}
                                    </Grid>
                                </CommandContext.Provider>
                            </RecordingContext.Provider>
                        </ChatContext.Provider>
                    </ZoomMediaContext.Provider>
                )}
            </div>
            <Dialog open={modalOpenPatientConnecting}>
                <Box padding={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                            <Typography color="primary" variant="h6">
                                Llamada entrante
                            </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                            <img src={Phone1} alt="phone" width={150} />
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                            <Typography>
                                Un paciente está realizando una videollamada. Acepte para confirmar el inicio de la
                                videollamada.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: "center" }}>
                            <Button variant="contained" color="primary" onClick={handleClickAceptarLlamada}>
                                Aceptar llamada
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Dialog>
        </Fragment>
    );
}

export default ZoomMainApp;
